/**
 * Shop the look.
 *
 * Blocks render their dot and their card together in the card column, because
 * only a block can read its own settings. On connect the dots are moved onto
 * the image, which is the one thing Liquid cannot do here.
 *
 * Selecting a dot shows that product's card in the column. Hovering a dot
 * reveals its compact card on the image, matching the original site.
 */

class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.dotHost = this.querySelector('[data-look-dots]');
    this.cards = Array.from(this.querySelectorAll('[data-look-card]'));

    this.relocateDots();
    this.buildPagination();

    this.dots = Array.from(this.querySelectorAll('[data-look-dot]'));
    if (this.cards.length === 0) return;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.activate(dot.dataset.lookId));
      // Hovering previews the product in the column too, so the two halves
      // never disagree about what is selected.
      dot.addEventListener('mouseenter', () => this.activate(dot.dataset.lookId));
      dot.addEventListener('focus', () => this.activate(dot.dataset.lookId));
    });

    this.pages.forEach((page) => {
      page.addEventListener('click', () => this.activate(page.dataset.lookId));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const ids = this.cards.map((card) => card.dataset.lookId);
      const current = ids.indexOf(this.activeId);
      if (current === -1) return;
      const step = event.key === 'ArrowRight' ? 1 : -1;
      this.activate(ids[(current + step + ids.length) % ids.length]);
      event.preventDefault();
    });

    this.activate(this.cards[0].dataset.lookId);
    this.flipEdgeDots();
    window.addEventListener('resize', () => this.flipEdgeDots(), { passive: true });
  }

  /**
   * Build the pagination from the rendered cards.
   *
   * It cannot be built in Liquid: block.id from a section loop comes back with
   * a "-1" suffix that does not match the id the block renders for itself, so
   * every control pointed at a card that did not exist and selecting one hid
   * everything. Deriving it from the cards keeps a single source of ids.
   */
  buildPagination() {
    const host = this.querySelector('[data-look-pagination]');
    this.pages = [];
    if (!host || this.cards.length < 2) return;

    this.cards.forEach((card, index) => {
      const page = document.createElement('button');
      page.type = 'button';
      page.className = 'look__page';
      page.setAttribute('role', 'tab');
      page.setAttribute('aria-label', `Product ${index + 1}`);
      page.dataset.lookId = card.dataset.lookId;
      host.appendChild(page);
      this.pages.push(page);
    });
  }

  /** Move every dot out of the card column and onto the image. */
  relocateDots() {
    if (!this.dotHost) return;
    this.querySelectorAll('[data-look-item] > [data-look-dot]').forEach((dot) => {
      this.dotHost.appendChild(dot);
    });
  }

  /** Dots near the right edge open their card leftward so it stays on screen. */
  flipEdgeDots() {
    if (!this.dotHost) return;
    const bounds = this.dotHost.getBoundingClientRect();
    if (bounds.width === 0) return;
    this.dots.forEach((dot) => {
      const box = dot.getBoundingClientRect();
      const fromLeft = box.left + box.width / 2 - bounds.left;
      dot.dataset.flip = fromLeft > bounds.width * 0.62 ? 'true' : 'false';
    });
  }

  activate(id) {
    if (!id || id === this.activeId) return;
    // Never act on an id that matches no card: that used to hide every card
    // and leave the column blank.
    if (!this.cards.some((card) => card.dataset.lookId === id)) return;
    this.activeId = id;

    this.cards.forEach((card) => {
      card.setAttribute('aria-hidden', card.dataset.lookId === id ? 'false' : 'true');
    });
    this.dots.forEach((dot) => {
      dot.setAttribute('aria-current', dot.dataset.lookId === id ? 'true' : 'false');
    });
    this.pages.forEach((page) => {
      page.setAttribute('aria-selected', page.dataset.lookId === id ? 'true' : 'false');
    });
  }
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
